<template>
	<div class="checkout">
		<!-- Order summary -->
		<div class="summary">
			<h2 class="summary__heading">{{ $t('configurator.summary') }}</h2>

			<table class="table">
				<tr>
					<td>{{ order.product.Title }}</td>
					<td>{{ getPrice(order.product.Price) }}</td>
				</tr>
				<tr>
					<td>{{ $t('configurator.groupSize') }}</td>
					<td>{{ order.details.peopleCount }} {{ $t('forms.persons') }}</td>
				</tr>
				<tr>
					<td>{{ $t('configurator.dateAndTime') }}</td>
					<td>{{ playDateAndTime }}</td>
				</tr>
			</table>

			<table class="table -price" v-if="order.extras && order.extras.length">
				<thead>
					<tr>
						<th>{{ $t('configurator.extras') }}</th>
					</tr>
				</thead>
				<tr v-for="extra in order.extras" :key="extra.id">
					<td>{{ extra.Title }}</td>
					<td>{{ extra.PriceType === 'Flat' ? 'pauschal' : 'p.P.' }}</td>
					<td>{{ getPrice(extra.Price) }}</td>
				</tr>
			</table>

			<table class="table -price" v-if="order.upsells && order.upsells.length">
				<thead>
					<tr>
						<th>{{ $t('configurator.specials') }}</th>
					</tr>
				</thead>
				<tr v-for="upsell in order.upsells" :key="upsell.id">
					<td>{{ upsell.Title }}</td>
					<td>{{ upsell.PriceType === 'Flat' ? 'pauschal' : 'p.P.' }}</td>
					<td>{{ getPrice(upsell.Price) }}</td>
				</tr>
			</table>

			<div class="summary__totals">
				<div class="summary__total-row">
					<span>{{ $t('configurator.pricePerPerson') }}</span>
					<span class="summary__price">{{ getPrice(pricePerPerson) }}</span>
				</div>
				<div class="summary__total-row -grand">
					<span>{{ $t('configurator.total') }}</span>
					<span class="summary__price">{{ getPrice(priceTotal) }}</span>
				</div>
			</div>
		</div>

		<!-- Action buttons -->
		<div class="checkout__actions">
			<styled-button v-if="isCheckoutAvailable && !isRequest" fullWidth @click.native="$emit('checkout')">
				<span class="checkout__btn-content">
					<img :src="paypalIcon" alt="PayPal" class="checkout__paypal-icon" />
					{{ $t('forms.bookNow') }}
				</span>
			</styled-button>
			<styled-button v-if="isCheckoutAvailable" fullWidth inverted @click.native="isRequest = !isRequest">
				{{ isRequest ? $t('configurator.backToCheckout') : $t('forms.sendRequest') }}
			</styled-button>
		</div>

		<!-- Request form (only visible when isRequest is true) -->
		<div v-if="isRequest" class="checkout__form">
			<div v-if="errors.length" class="checkout__errors">
				<b class="block m-b-xs">{{ $t('forms.requiredFields') }}</b>
				<span v-for="fieldKey in errors" :key="fieldKey" class="block m-b-xs">
					<span v-html="$t(formFields[fieldKey].label)" />
				</span>
			</div>
			<form class="flex" @submit.prevent="submit(formFields, inputData, isRequest)">
				<div v-for="(formField, name) in formFields" :key="name" :class="`${formField.className} p-xs`">
					<input v-if="formField.type === 'checkbox'" v-on:focus="$emit('customer-form-interaction')" type="checkbox"
						v-model="inputData[name]" :id="name" class="float-left" />
					<label :for="name" class="m-b-xxs block">
						<span v-html="$t(formField.label, { link: formField.link })"></span>
					</label>
					<styled-input v-if="isInput(formField.type)" v-on:focus="$emit('customer-form-interaction')"
						:type="formField.type" :name="name" :pattern="formField.pattern || false"
						:title="$t(formField.title) || false" v-model="inputData[name]" :required="formField.required"
						:id="name" />
					<styled-textarea v-if="formField.type === 'textarea'" v-model="inputData[name]" />
				</div>
				<styled-button fullWidth type="submit">
					{{ $t('forms.sendRequest') }}
				</styled-button>
			</form>
		</div>
	</div>
</template>
<script>
// isInput
import ConfiguratorMixin from '@/mixins/configurator';
import styledTextarea from '@/components/common/styledTextarea';
import styledInput from '@/components/common/styledInput';
import styledButton from '@/components/common/styledButton';
import paypalIcon from '@/assets/images/paypal.svg';
export default {
	name: 'customer-form',

	components: {
		styledInput,
		styledTextarea,
		styledButton,
	},

	computed: {
		playDateAndTime() {
			const { playDate, playTime } = this.order.details;
			const time = playTime?.match(/^\d\d:\d\d/)?.[0] ?? '';
			const date = playDate?.replace(/-/g, '/') ?? '';
			const frontendValue = this.order.product.BookingTimes?.find(
				(t) => t.BookableTime.match(/^\d\d:\d\d/)?.[0] === playTime,
			)?.FrontendValue;

			if (!frontendValue) {
				return new Date(`${date} ${time}`).toLocaleString(undefined, {
					year: 'numeric',
					month: 'numeric',
					day: 'numeric',
					hour: 'numeric',
					minute: 'numeric',
				});
			}

			return `${new Date(date).toLocaleString(undefined, {
				year: 'numeric',
				month: 'numeric',
				day: 'numeric',
			})} ${frontendValue}`;
		},

		/**
		 * Paid checkout via Snipcart is only allowed when the play date
		 * is more than 7 days in the future. Otherwise the customer must
		 * use the inquiry/request flow.
		 */
		isCheckoutAvailable() {
			const { playDate } = this.order.details;
			if (!playDate) return false;

			const eventDate = new Date(playDate.replace(/-/g, '/'));
			const now = new Date();
			// Strip time portion so we compare calendar days only
			now.setHours(0, 0, 0, 0);
			eventDate.setHours(0, 0, 0, 0);

			const msPerDay = 24 * 60 * 60 * 1000;
			const daysUntilEvent = (eventDate - now) / msPerDay;
			return daysUntilEvent > 7;
		},
	},

	mixins: [ConfiguratorMixin],

	props: {
		formFields: {
			type: Object,
			required: true,
		},

		order: {
			type: Object,
			required: true,
		},

		priceTotal: {
			type: Number,
			required: true,
		},

		pricePerPerson: {
			type: Number,
			required: true,
		},

		mode: {
			type: String,
			required: true,
		},
	},

	data() {
		return {
			paypalIcon,
			inputData: {},
			isRequest: false,
			errors: [],
		};
	},

	mounted() {
		window.scroll(0, 0);
		if (this.mode === 'inquiry' || !this.isCheckoutAvailable) {
			this.isRequest = true;
		}
	},

	methods: {
		checkForRequiredFields(formFields, inputData) {
			return Object.keys(formFields).filter((key) => formFields[key].required && !inputData[key]);
		},

		submit(formFields, inputData, isRequest) {
			const errors = this.checkForRequiredFields(formFields, inputData);
			if (!isRequest) {
				this.$emit('form-data-filled', {
					...inputData,
					isRequest,
				});
			}
			if (isRequest && !errors.length) {
				this.$emit('form-data-filled', {
					...inputData,
					isRequest,
				});
				this.errors = [];
				return;
			}
			this.errors = errors;
		},
	},
};
</script>

<style lang="scss" scoped>
.checkout {
	max-width: 600px;
	margin: 0 auto;
	padding: 2rem 1rem;

	&__actions {
		padding: 1.5rem 0;
		display: flex;
		flex-direction: column;
		gap: 0.5rem;
	}

	&__errors {
		background: #c0392b;
		color: #fff;
		padding: 0.75rem 1rem;
		border-radius: 4px;
		margin-bottom: 1rem;
	}

	&__btn-content {
		display: flex;
		align-items: center;
		justify-content: center;
	}

	&__paypal-icon {
		height: 1.2em;
		margin-right: 0.4rem;
	}

	&__form {
		border-top: 1px solid #eee;
		padding-top: 1.5rem;
	}
}

.summary {
	&__heading {
		font-size: 1.25rem;
		margin: 0 0 1rem;
		padding-bottom: 0.75rem;
		border-bottom: 1px solid #eee;
	}

	&__totals {
		border-top: 2px solid #eee;
		margin-top: 0.75rem;
		padding-top: 0.75rem;
	}

	&__total-row {
		display: flex;
		justify-content: space-between;
		padding: 0.25rem 0;
		font-size: 0.95rem;
		color: #555;

		&.-grand {
			font-size: 1.35rem;
			font-weight: bold;
			color: #000;
			padding-top: 0.5rem;
		}
	}

	&__price {
		font-weight: bold;
	}
}

.table {
	width: 100%;
	line-height: 1.25;
	border-collapse: collapse;

	th {
		text-align: left;
		font-weight: 600;
		font-size: 0.85rem;
		text-transform: uppercase;
		letter-spacing: 0.03em;
		color: #888;
		padding: 0.75rem 0 0.35rem 0;
	}

	td {
		padding: 0.4rem 0;
		font-size: 0.95rem;

		&:first-child {
			padding-right: 1rem;
			word-break: break-word;
			width: 50%;
		}

		&:last-child {
			text-align: right;
		}
	}

	&.-price {
		td {
			&:first-child {
				max-width: 40%;
			}

			&:last-child {
				padding-left: 1rem;
			}
		}
	}
}
</style>
